import * as React from "react";
import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import GlobalStyles from "@mui/material/GlobalStyles";
import Button from "@mui/material/Button";
import { useT } from "talkr";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
//@ts-ignore
import Flag from "react-world-flags";
import Background from "./assets/Background_blue.png";

// The wedding ceremony livestream target date: 02.03.2026 12:00 CET (UTC+1)
// CET is UTC+1, so 12:00 CET = 11:00 UTC
const TARGET_DATE_UTC = new Date("2026-03-02T11:00:00Z");

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function getTimeLeft(): TimeLeft | null {
  const now = new Date();
  const diff = TARGET_DATE_UTC.getTime() - now.getTime();

  if (diff <= 0) return null;

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds };
}

const LOCALE_TO_BCP47: Record<string, string> = {
  en: "en-GB",
  tw: "zh-TW",
  sk: "sk",
  cs: "cs",
  es: "es",
  it: "it",
  el: "el",
  tr: "tr",
};

function formatLocalDate(talkrLocale: string): string {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  };
  const bcp47 = LOCALE_TO_BCP47[talkrLocale] ?? talkrLocale;
  return TARGET_DATE_UTC.toLocaleString(bcp47, options);
}

export default function LivestreamPage() {
  const { T, setLocale, locale } = useT();
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(getTimeLeft());

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(getTimeLeft());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      <GlobalStyles
        styles={{
          body: {
            backgroundImage: `url(${Background})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundAttachment: "fixed",
            minHeight: "100vh",
          },
        }}
      />

      <Box
        aria-hidden
        sx={{
          position: "fixed",
          inset: 0,
          backgroundColor: alpha("#ffffffff", 0.85),
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      <Container
        maxWidth="md"
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: { xs: 3, sm: 5, md: 6 },
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontFamily: "FUTURAM",
            fontWeight: 700,
            letterSpacing: { xs: 1, sm: 2, md: 3 },
            mb: { xs: 2, sm: 3, md: 4 },
            textAlign: "center",
            color: "primary.contrastText",
          }}
        >
          {T("livestream.title")}
        </Typography>

        {/* Countdown */}
        <Box sx={{ mb: { xs: 3, sm: 4, md: 5 }, textAlign: "center" }}>
          {timeLeft ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: { xs: 1.5, sm: 3, md: 4 },
                  mb: 2,
                }}
              >
                {[
                  { value: timeLeft.days, label: T("livestream.days") },
                  { value: timeLeft.hours, label: T("livestream.hours") },
                  { value: timeLeft.minutes, label: T("livestream.minutes") },
                  { value: timeLeft.seconds, label: T("livestream.seconds") },
                ].map((item) => (
                  <Box key={item.label} sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontFamily: "FUTURAM",
                        fontWeight: 700,
                        fontSize: { xs: "1.8rem", sm: "2.5rem", md: "3rem" },
                        color: "primary.contrastText",
                        lineHeight: 1,
                      }}
                    >
                      {String(item.value).padStart(2, "0")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "FUTURAM",
                        color: "primary.contrastText",
                        fontSize: { xs: "0.7rem", sm: "0.85rem", md: "1rem" },
                        mt: 0.5,
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: "primary.contrastText",
                  opacity: 0.7,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                }}
              >
                {T("livestream.your_local_time")} {formatLocalDate(locale)}!
              </Typography>
            </>
          ) : (
            <Typography
              variant="h5"
              sx={{
                fontFamily: "FUTURAM",
                color: "primary.contrastText",
              }}
            >
              {T("livestream.live_now")}
            </Typography>
          )}
        </Box>

        {/* YouTube Embed */}
        {/*
          ============================================================
          HOW TO CUSTOMIZE THE YOUTUBE LIVESTREAM LINK:
          ============================================================
          Replace the YouTube video ID in the URL below with your own.

          Current placeholder URL:
            https://www.youtube.com/embed/YOUR_VIDEO_ID

          To get your video ID:
            1. Go to your YouTube livestream or video
            2. Copy the video ID from the URL
               e.g. https://www.youtube.com/watch?v=dQw4w9WgXcQ
               The video ID is: dQw4w9WgXcQ
            3. Replace YOUR_VIDEO_ID below with your actual video ID:
               https://www.youtube.com/embed/dQw4w9WgXcQ

          You can also add parameters like:
            ?autoplay=1  — to auto-play
            ?mute=1      — to start muted
            ?rel=0       — to hide related videos

          Example with parameters:
            https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1&mute=1
          ============================================================
        */}
        <Box
          sx={{
            width: "100%",
            maxWidth: 800,
            aspectRatio: "16/9",
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: 3,
            backgroundColor: "#000",
          }}
        >
          <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/Nk9sAAwqpmE"
            title={T("livestream.title")}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{ border: "none", display: "block" }}
          />
        </Box>

        <Typography
          variant="body2"
          sx={{
            mt: { xs: 2, sm: 3 },
            color: "primary.contrastText",
            opacity: 0.7,
            textAlign: "center",
            fontSize: { xs: "0.95rem", sm: "1.1rem" },
          }}
        >
          {T("livestream.description")}
        </Typography>

        <Button
          variant="outlined"
          onClick={() => navigate("/")}
          sx={{
            mt: { xs: 3, sm: 4 },
            fontFamily: "FUTURAM",
            color: "primary.contrastText",
            borderColor: "primary.contrastText",
            "&:hover": {
              borderColor: "primary.contrastText",
              backgroundColor: alpha("#000000", 0.1),
            },
          }}
        >
          {T("livestream.home")}
        </Button>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "nowrap",
            justifyContent: "center",
            alignItems: "center",
            mt: { xs: 2, sm: 3 },
            gap: 1,
          }}
        >
          {[
            { locale: "en", code: "GB" },
            { locale: "tw", code: "TW" },
            { locale: "sk", code: "sk" },
            { locale: "cs", code: "CZ" },
            { locale: "es", code: "CO" },
            { locale: "it", code: "IT" },
            { locale: "el", code: "GR" },
            { locale: "tr", code: "TR" },
          ].map(({ locale, code }) => (
            <IconButton
              key={locale}
              sx={{
                p: { xs: 0.2, sm: 0.5, md: 1 },
                width: { xs: 28, sm: 40, md: 48 },
                height: { xs: 28, sm: 40, md: 48 },
              }}
              onClick={() => setLocale(locale)}
            >
              <Flag
                code={code}
                style={{ width: "100%", height: "100%", objectFit: "contain" as const }}
              />
            </IconButton>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
